import { faGasPump } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  useBids,
  useCollections,
  useListings,
  useTokens,
} from '@reservoir0x/reservoir-kit-ui'
import { AcceptBid, Bid, List } from 'components/buttons'
import { BuyNow } from 'components/buttons'
import AddToCart from 'components/buttons/AddToCart'
import CancelBid from 'components/buttons/CancelBid'
import CancelListing from 'components/buttons/CancelListing'
import QuantitySelector from 'components/custom/modals/QuantitySelector'
import { Button, Flex, Tooltip, Text } from 'components/primitives'
import { useRouter } from 'next/router'
import { ComponentPropsWithoutRef, FC, useState, useEffect } from 'react'
import { MutatorCallback } from 'swr'
import { useAccountPrivyWallet } from 'hooks/auth/useAccountPrivyWallet'
import { MARKETPLACE_SOURCE, MAX_QUANTITY_PURCHASE } from 'utils/constants'
import { getParentSystemIdFromContract } from 'config/routes'
import { useMergedTokenData } from 'hooks/useMergedTokenData'
import { ConnectedWallet } from '@privy-io/react-auth'
import supportedChains from 'utils/chains'
import { useAuthWallets } from 'hooks/auth/useAuthWallets'
import { sortTokenFloorListings } from 'utils/mergeChainV2'
import { InfoBanner } from 'components/custom/banners/InfoBanner'

type Props = {
  token: ReturnType<typeof useTokens>['data'][0]
  collection: ReturnType<typeof useCollections>['data'][0] | null
  offer?: ReturnType<typeof useBids>['data'][0]
  listing?: ReturnType<typeof useListings>['data'][0]
  isOwner: boolean
  mutate?: MutatorCallback
  account?: ConnectedWallet
}

export const TokenActions: FC<Props> = ({
  token,
  offer,
  listing,
  isOwner,
  mutate,
  account,
}) => {
  const router = useRouter()
  const { changeAccountActiveWallet } = useAccountPrivyWallet()
  const { ownershipPrivyWallet } = useAuthWallets()
  const bidOpenState = useState(true)
  const [path, _] = router.asPath.split('?')
  const queryBidId = router.query.bidId as string
  const deeplinkToAcceptBid = router.query.acceptBid === 'true'
  const is1155 = token?.token?.kind === 'erc1155'
  const [quantity, setQuantity] = useState<number>(1)
  const { ownershipWallet } = useAuthWallets()

  const myChainId = Number(ownershipPrivyWallet?.chainId.split(':')[1])
  const myChain = supportedChains.find((chain) => chain.id === myChainId)
  const systemId = getParentSystemIdFromContract(token.token?.contract || '')
  const tokenId = token.token?.tokenId

  const mainnetChain = supportedChains.find((chain) => chain.id === 1)
  const isPirateCollection = systemId?.startsWith('game.piratenation.piratenft')

  if (!systemId || !tokenId) {
    return null
  }

  const isBelowSolverCapacity =
    BigInt(token?.market?.floorAsk?.price?.amount?.raw || 0) <
    25000000000000000000n
  const isBlurSource = token?.market?.floorAsk?.source?.name === 'Blur'
  const intentFillingEnabled = !is1155 && isBlurSource && isBelowSolverCapacity

  const showAcceptOffer =
    !is1155 &&
    token?.market?.topBid?.id !== null &&
    token?.market?.topBid?.id !== undefined &&
    isOwner &&
    token?.token?.owner
      ? true
      : false

  const isTopBidder =
    token?.market?.topBid?.maker?.toLowerCase() ===
    account?.address?.toLowerCase()
  const isListed = token ? token?.market?.floorAsk?.id !== null : false

  const { data: listingsData } = useMergedTokenData(
    systemId,
    tokenId,
    'listings',
    {
      sortBy: 'price',
      limit: 1000,
    }
  )
  const calculatedListingsQuantityRemaining =
    listingsData?.reduce(
      (acc, curr) => acc + (curr.quantityRemaining || 0),
      0
    ) || 0

  const listingsQuantityRemaining =
    calculatedListingsQuantityRemaining < MAX_QUANTITY_PURCHASE
      ? calculatedListingsQuantityRemaining
      : MAX_QUANTITY_PURCHASE

  const offerIsOracleOrder = offer?.isNativeOffChainCancellable

  const listingIsOracleOrder = listing?.isNativeOffChainCancellable

  // Get lowest price listing that is not your own listing
  // sort by floor ask price + listing creation date
  const lowestPriceListing = sortTokenFloorListings(
    listingsData,
    ownershipWallet?.address
  )[0]

  const buttonCss: ComponentPropsWithoutRef<typeof Button>['css'] = {
    width: '100%',
    flexGrow: 1,
    height: 52,
    justifyContent: 'center',
    minWidth: 250,
  }

  return (
    <Flex
      align="center"
      css={{
        flexDirection: 'row',
        gap: '$2',
        width: '100%',
        justifyItems: 'flex-start',
        '@media (max-width: 1100px)': {
          alignItems: 'flex-start',
          flexDirection: 'column',
        },
      }}
    >
      {isOwner && !is1155 && (
        <List
          token={token}
          mutate={mutate}
          buttonCss={buttonCss}
          buttonChildren={
            token?.market?.floorAsk?.price?.amount?.decimal
              ? 'Create New Listing'
              : 'List for Sale'
          }
        />
      )}
      {(!isOwner || is1155) &&
        isListed &&
        token?.market?.floorAsk?.price?.amount && (
          <>
            <Flex css={{ gap: '$2', width: '100%' }}>
              {is1155 ? (
                <QuantitySelector
                  quantity={quantity}
                  min={1}
                  max={listingsQuantityRemaining || 0}
                  setQuantity={(updatedQuantity: number) => {
                    setQuantity(updatedQuantity)
                  }}
                />
              ) : null}
              {lowestPriceListing && (
                <BuyNow
                  analyticsId="click buy now on token item details page"
                  token={token}
                  tokenId={tokenId}
                  contract={lowestPriceListing?.contract}
                  executionMethod={intentFillingEnabled ? 'intent' : undefined}
                  buttonCss={buttonCss}
                  buttonChildren={
                    <Text
                      css={{ fontWeight: 600, fontSize: 16, color: 'white' }}
                    >
                      {is1155 ? `Buy ${quantity} Now` : 'Buy Now'}
                    </Text>
                  }
                  source={MARKETPLACE_SOURCE}
                  mutate={mutate}
                  quantity={quantity}
                  disabled={listingsQuantityRemaining === 0}
                />
              )}
            </Flex>
            {/* {!is1155 && (
            <AddToCart
                token={token}
                buttonCss={{
                  width: 52,
                  p: 0,
                  justifyContent: 'center',
                }}
                buttonProps={{ corners: 'square' }}
              />
            )} */}
          </>
        )}
      {showAcceptOffer && (
        <AcceptBid
          analyticsId="click marketplace accept - on token item details page"
          tokenId={token.token?.tokenId}
          bidId={queryBidId}
          collectionId={token?.token?.contract}
          openState={
            isOwner && (queryBidId || deeplinkToAcceptBid)
              ? bidOpenState
              : undefined
          }
          mutate={mutate}
          buttonCss={buttonCss}
          buttonChildren="Accept Offer"
        />
      )}

      {(!isOwner || is1155) && (
        <Bid
          tokenId={token?.token?.tokenId}
          collectionId={lowestPriceListing?.contract}
          mutate={mutate}
          buttonCss={buttonCss}
        />
      )}

      {isTopBidder && !is1155 && (
        <CancelBid
          collectionId={token?.token?.contract}
          bidId={token?.market?.topBid?.id as string}
          mutate={mutate}
          trigger={
            <Flex>
              {!offerIsOracleOrder ? (
                <Tooltip
                  content={
                    <Text style="body3" as="p">
                      Cancelling this order requires gas.
                    </Text>
                  }
                >
                  <Button
                    css={{
                      color: '$red11',
                      width: '100%',
                      height: 52,
                      justifyContent: 'center',
                      minWidth: 'max-content',
                      '@sm': {
                        maxWidth: 250,
                      },
                    }}
                    color="gray3"
                  >
                    <FontAwesomeIcon
                      color="#697177"
                      icon={faGasPump}
                      width="16"
                      height="16"
                    />
                    Cancel Offer
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  css={{
                    color: '$red11',
                    width: '100%',
                    height: 52,
                    justifyContent: 'center',
                    minWidth: 'max-content',
                    '@sm': {
                      maxWidth: 250,
                    },
                  }}
                  color="gray3"
                >
                  Cancel Offer
                </Button>
              )}
            </Flex>
          }
        />
      )}

      {isOwner && isListed && !is1155 && (
        <CancelListing
          collectionId={token.token?.contract}
          listingId={token?.market?.floorAsk?.id as string}
          mutate={mutate}
          trigger={
            <Flex
              onClick={() => {
                changeAccountActiveWallet()
              }}
            >
              {!listingIsOracleOrder ? (
                <Tooltip
                  content={
                    <Text style="body3" as="p">
                      Cancelling this order requires gas.
                    </Text>
                  }
                >
                  <Button
                    css={{
                      ...buttonCss,
                      color: '$red11',
                      minWidth: '150px',
                    }}
                    color="gray3"
                  >
                    <FontAwesomeIcon
                      color="#697177"
                      icon={faGasPump}
                      width="16"
                      height="16"
                    />
                    Cancel Listing
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  css={{
                    ...buttonCss,
                    color: '$red11',
                    minWidth: '150px',
                    justifyContent: 'center',
                  }}
                  color="gray3"
                >
                  Cancel Listing
                </Button>
              )}
            </Flex>
          }
        />
      )}
    </Flex>
  )
}
